import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import api from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [requiresSecondFactor, setRequiresSecondFactor] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryPhoneNumber, setRecoveryPhoneNumber] = useState('');
  const [secondFactorCode, setSecondFactorCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setValidating(false);
        return;
      }

      try {
        const response = await api.get<{ valid: boolean; requiresSecondFactor?: boolean }>(`/api/Auth/validate-reset-token?token=${token}`);
        setTokenValid(response.valid);
        setRequiresSecondFactor(response.requiresSecondFactor !== false);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async () => {
    setError(null);

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (requiresSecondFactor && !secondFactorCode.trim()) {
      setError('SMS verification code is required');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/Auth/reset-password-alias', {
        token,
        newPassword,
        newUsername: newUsername.trim() || undefined,
        secondFactorCode: requiresSecondFactor ? secondFactorCode.trim() : '',
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    if (!token) return;
    setError(null);
    setSendingCode(true);
    try {
      await api.post('/api/Auth/send-reset-second-factor-code', {
        token,
        phoneNumber: recoveryPhoneNumber.trim() || undefined,
      });
      setCodeSent(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send SMS verification code.');
    } finally {
      setSendingCode(false);
    }
  };

  if (validating) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
            <LockResetIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" component="h1">
              {requiresSecondFactor ? 'Reset Password' : 'Set your password'}
            </Typography>
          </Box>

          {!tokenValid ? (
            <Box textAlign="center">
              <Alert severity="error" sx={{ mb: 2 }}>
                This password reset link is invalid or has expired.
              </Alert>
              <Button variant="contained" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </Box>
          ) : success ? (
            <Box textAlign="center">
              <Alert severity="success" sx={{ mb: 2 }}>
                {requiresSecondFactor
                  ? 'Your password has been reset successfully!'
                  : 'Your password has been set. You can sign in with it now.'}
              </Alert>
              <Button variant="contained" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {!requiresSecondFactor && (
                <TextField
                  fullWidth
                  label="Username (optional)"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  margin="normal"
                  disabled={loading}
                  autoComplete="off"
                  helperText="Leave blank to keep the username we generated. At least 3 letters and 2 numbers."
                />
              )}

              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                disabled={loading}
                helperText="At least 8 characters"
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                disabled={loading}
                onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
              />

              {requiresSecondFactor && (
                <>
                  <TextField
                    fullWidth
                    label="Recovery Phone Number (if no verified phone on file)"
                    value={recoveryPhoneNumber}
                    onChange={(e) => setRecoveryPhoneNumber(e.target.value)}
                    margin="normal"
                    disabled={loading || sendingCode}
                    helperText="Example: +12345678900"
                  />

                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={handleSendCode}
                      disabled={sendingCode || loading}
                    >
                      {sendingCode ? <CircularProgress size={20} /> : (codeSent ? 'Resend SMS Code' : 'Send SMS Code')}
                    </Button>
                    {codeSent && (
                      <Alert severity="info" sx={{ py: 0, flex: 1 }}>
                        SMS code sent. Enter it below to continue.
                      </Alert>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label="SMS Verification Code"
                    value={secondFactorCode}
                    onChange={(e) => setSecondFactorCode(e.target.value)}
                    margin="normal"
                    disabled={loading}
                    helperText="Required second factor for password reset"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                  />
                </>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : (requiresSecondFactor ? 'Reset Password' : 'Set password')}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword;
